import editicon from "../../../../assets/img/schooladmin/icons/edit.png";
import deleteicon from "../../../../assets/img/schooladmin/icons/delete.png";
import { Link } from "react-router-dom";
import DeleteModal from "../../../common/DeleteModal";
import { useState } from "react";
import { Form } from "react-bootstrap";
import "../../SuperAdmin/toggle.css";
import { useNavigate } from "react-router-dom";

/**
 * @typedef {Object} StudentTableRowProps
 * @property {Object} student - The student object
 * @property {(studentId: string) => Promise<void>} onDelete - Function to delete a student
 * @property {boolean} deleteLoading - Whether delete is in progress
 * @property {string} deleteError - Error message from delete operation
 * @property {boolean} deleteSuccess - Whether delete was successful
 * @property {() => void} resetDeleteState - Function to reset delete state
 * @property {() => void} onDeleteSuccess - Callback after successful deletion
 * @property {(studentId: string) => Promise<void>} onToggle - Function to toggle student status
 * @property {boolean} toggleLoading - Whether toggle is in progress
 * @property {string} toggleError - Error message from toggle operation
 * @property {boolean} toggleSuccess - Whether toggle was successful
 * @property {() => void} resetToggleState - Function to reset toggle state
 * @property {() => void} onToggleSuccess - Callback after successful toggle
 */

/**
 * @param {StudentTableRowProps} props
 */
const StudentTableRow = ({
  student,
  onDelete,
  deleteLoading,
  deleteError,
  deleteSuccess,
  resetDeleteState,
  onDeleteSuccess,
  onToggle,
  toggleLoading,
  toggleError,
  toggleSuccess,
  resetToggleState,
  onToggleSuccess,
  isSelected = false,
  onToggleSelect,
  RoleGuard,
}) => {
  const fullName = student?.name || "";
  const isActive = student?.status === "1" || student?.status === 1;
  const [showModal, setShowModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showToggleModal, setShowToggleModal] = useState(false);
  const [isToggling, setIsToggling] = useState(false);

  const navigate = useNavigate();
  // Delete handlers
  const handleShow = () => {
    resetDeleteState();
    setShowModal(true);
  };

  const handleClose = () => {
    resetDeleteState();
    setShowModal(false);
    setIsDeleting(false);
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    await onDelete(student.id);
  };

  const handleSuccessContinue = () => {
    setShowModal(false);
    setIsDeleting(false);
    resetDeleteState();
    onDeleteSuccess();
  };

  // Toggle handlers
  const handleToggleShow = () => {
    resetToggleState();
    setShowToggleModal(true);
  };

  const handleToggleClose = () => {
    resetToggleState();
    setShowToggleModal(false);
    setIsToggling(false);
  };

  const handleToggle = async () => {
    setIsToggling(true);
    await onToggle(student.id);
  };

  const handleToggleSuccessContinue = () => {
    setShowToggleModal(false);
    setIsToggling(false);
    resetToggleState();
    onToggleSuccess();
  };

  // Only show loading/error/success for this specific row
  const showLoading = isDeleting && deleteLoading;
  const showError = isDeleting && deleteError;
  const showDeleteSuccess = isDeleting && deleteSuccess;

  const showToggleLoading = isToggling && toggleLoading;
  const showToggleError = isToggling && toggleError;
  const showToggleSuccess = isToggling && toggleSuccess;

  return (
    <tr
      key={student.id}
      style={{
        cursor: "pointer",
      }}
      onClick={() => {
        navigate(`/school-admin/school/report/students/${student?.id}`);
      }}
    >
      <RoleGuard permission="dashboard_assign_licenses">
        <td onClick={(e) => e.stopPropagation()}>
          <Form.Check
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggleSelect?.(student.id)}
            aria-label={`Select ${fullName}`}
          />
        </td>
      </RoleGuard>
      <td className="custom-id">{student?.username || "N/A"}</td>
      <td>
        <div className="custom-name-cell">
          <span className="custom-name">{student?.name}</span>
        </div>
      </td>
      <td>{student?.email}</td>
      <td>
        {student?.assigned_teacher?.firstName
          ? student?.assigned_teacher?.firstName +
            " " +
            student?.assigned_teacher?.lastName
          : "-"}
      </td>
      <RoleGuard permission="students_action">
        <td onClick={(e) => e.stopPropagation()}>
          <div className="toggle-switch">
            <input
              type="checkbox"
              id={`switch-${student.id}`}
              checked={isActive}
              onChange={handleToggleShow}
            />
            <label
              htmlFor={`switch-${student.id}`}
              className="slider"
              style={{
                backgroundColor: isActive ? "#008EF4" : "",
              }}
            ></label>
          </div>
        </td>
      </RoleGuard>
      <RoleGuard permission="students_edit">
        <td onClick={(e) => e.stopPropagation()}>
          <RoleGuard permission="students_edit">
            <Link
              to={`/school-admin/students/edit/${student?.id}`}
              className="tchrdashboard-tableicn"
            >
              <img src={editicon} alt="" />
            </Link>
          </RoleGuard>

          <RoleGuard permission="students_edit">
            <Link
              onClick={() => handleShow()}
              className="tchrdashboard-edittableicn"
            >
              <img src={deleteicon} alt="" />
            </Link>
          </RoleGuard>
          <DeleteModal
            showModal={showModal}
            handleClose={handleClose}
            message={`Are you sure you want to delete ${fullName}? This action cannot be undone.`}
            loading={showLoading}
            handleDelete={handleDelete}
            error={showError ? deleteError : ""}
            success={showDeleteSuccess}
            successMessage={`${fullName} has been deleted successfully.`}
            onSuccessContinue={handleSuccessContinue}
          />
          <DeleteModal
            showModal={showToggleModal}
            handleClose={handleToggleClose}
            message={`Are you sure you want to ${isActive ? "deactivate" : "activate"} ${fullName}?`}
            loading={showToggleLoading}
            handleDelete={handleToggle}
            error={showToggleError ? toggleError : ""}
            success={showToggleSuccess}
            successMessage={`${fullName} has been ${isActive ? "deactivated" : "activated"} successfully.`}
            onSuccessContinue={handleToggleSuccessContinue}
            customSuccessTitle={
              isActive ? "Deactivated Successfully" : "Activated Successfully"
            }
            customErrorTitle="Toggle Failed"
            customConfirmTitle="Confirm Status Change"
            customActionButtonText={isActive ? "Deactivate" : "Activate"}
            customLoadingButtonText={
              isActive ? "Deactivating..." : "Activating..."
            }
          />
        </td>
      </RoleGuard>
    </tr>
  );
};

export default StudentTableRow;
